"use client";

import { isbot } from "isbot";
import { useEffect, useRef } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { useUnauthorizedEventService } from "~/services/api/services/event";
import { ERROR_BEACON_EVENT_NAME } from "~/services/api/web-event-beacon";
import { AppConfig } from "~/utils/AppConfig";
import { APP_VERSION } from "~/services/api/config";
import { sendWebEvent } from "~/lib/webevents/WebEventsClient";
import { handleChunkLoadError } from "~/utils/chunkErrorRecovery";
import useUserId from "~/hooks/useUserId";
import {
    allowlistedErrorName,
    redactPersonalData,
} from "~/utils/errorTelemetry";

// Known noise: browser extensions, in-app browsers, WebKit internals, transient network issues
const EXCLUDE_MESSAGES = [
    "Can't find variable: lastScrollEventTimestamp",
    "window.webkit.messageHandlers",
    "UserMessageHandlersNamespace",
    "runtime.sendMessage",
    "NetworkError",
    // Facebook's Android in-app browser calling its Java bridge from its own script
    "Error invoking postMessage",
];

// Third-party scripts that generate uncaught errors outside our control
const EXCLUDE_SOURCES = [
    "static.klaviyo.com",
    "static-tracking.klaviyo.com",
    "webkit-masked-url",
];

/**
 * How many times one error is reported to Datadog before the rest are
 * suppressed.
 *
 * The cap is what replaces the old "drop every repeat": three records, each
 * numbered, answer "how many times did this happen to them" directly, and a
 * burst of any size still costs at most three. Deliberately not a timer that
 * reports a total when the burst goes quiet — that owed one extra record per
 * burst to learn a number past the third repeat that nobody acts on.
 *
 * Datadog only. See `FIRST_OCCURRENCE` for what the repeats are allowed to
 * cost everywhere else.
 */
const MAX_REPORTED_OCCURRENCES = 3;

/**
 * The one occurrence that is reported everywhere, at full severity.
 *
 * Repeats are a weaker signal than the first sighting and are held to the two
 * limits that follow from that:
 *
 * - **BigQuery gets only this one.** Every repeat is an extra POST from the
 *   customer's browser, and the old suppressor never sent them, so counting
 *   them now would silently change what the product analytics tables contain
 *   — a burst would read as several separate faults.
 * - **Repeats log at `warn`.** They are the same fault already recorded at
 *   `error` under the same message, so letting them through at `error` would
 *   multiply the volume every `status:error` monitor is thresholded on
 *   without a single new fault behind it. `occurrence` still carries the
 *   count for anyone who wants it.
 */
const FIRST_OCCURRENCE = 1;

/** Stands in for a rejection whose value carries no readable message. */
const UNHANDLED_REJECTION_MESSAGE = "Unhandled promise rejection";

/**
 * The message/stack pair both sinks read, for a rejection value we did not
 * author.
 *
 * `reason` is whatever was handed to `Promise.reject`, and reading `.message`
 * off it only answers for the `Error` case. A string reason — what a rejected
 * third-party SDK call most often produces — left both halves `undefined`, and
 * the empty-payload guard in `occurrenceToReport` then dropped the rejection
 * before it reached Datadog or BigQuery. It also left `handleChunkLoadError`
 * nothing to match on, so a chunk failure rejected as a string never triggered
 * the reload it exists for.
 *
 * Guarded, because reading a property off a value we did not create can throw
 * instead of answering, and a throw here would take the rest of the handler —
 * chunk recovery and the previously installed listener — down with it.
 */
function describeRejection(reason: unknown): {
    msg: string;
    stack: string | null;
    /**
     * The rejection's *own* `message`, absent unless it carries one.
     *
     * Kept apart from `msg` because only chunk recovery reads it, and chunk
     * recovery reloads the page. `msg` falls back to the raw string and to
     * `UNHANDLED_REJECTION_MESSAGE` so the record is never empty, and feeding
     * either of those to the matcher would arm `window.location.reload()` on
     * text no `Error` ever owned — a customer mid-checkout losing a filled
     * card form to a reload we newly decided to trigger. Reporting is allowed
     * to guess at a description; reloading is not.
     */
    reasonMessage?: string;
} {
    try {
        if (typeof reason === "string" && reason) {
            return { msg: reason, stack: null };
        }

        // Read structurally rather than via `instanceof`: that is realm-local,
        // and the card fields run in a Chargebee iframe.
        const { message, stack } = (reason ?? {}) as {
            message?: unknown;
            stack?: unknown;
        };
        const hasOwnMessage = typeof message === "string" && Boolean(message);

        return {
            msg: hasOwnMessage ? message : UNHANDLED_REJECTION_MESSAGE,
            stack: typeof stack === "string" ? stack : null,
            reasonMessage: hasOwnMessage ? message : undefined,
        };
    } catch {
        return { msg: UNHANDLED_REJECTION_MESSAGE, stack: null };
    }
}

/**
 * The `object` discriminators of the ChargeBee and Stripe resources checkout
 * handles. A closed list rather than an identifier shape: a value that is not
 * ours can look like an identifier and still be a name.
 */
const REJECTION_OBJECT_TYPES: ReadonlySet<string> = new Set([
    "error",
    "payment_attempt",
    "payment_intent",
    "payment_method",
    "setup_intent",
]);

/**
 * Logged in place of a discriminator outside the list, so a new type shows up
 * in Datadog without its value, which is not ours, reaching the log.
 */
const UNLISTED_REJECTION_OBJECT = "unlisted";

/**
 * Which API resource a rejection with no message was, so an SDK rejecting
 * with a raw resource is told apart from every other silent rejection.
 * Guarded like `describeRejection`: the value is not ours.
 */
function readRejectionObjectType(reason: unknown): string | undefined {
    try {
        if (typeof reason !== "object" || reason === null) return undefined;

        const { object } = reason as { object?: unknown };
        if (typeof object !== "string") return undefined;

        return REJECTION_OBJECT_TYPES.has(object)
            ? object
            : UNLISTED_REJECTION_OBJECT;
    } catch {
        return undefined;
    }
}

/**
 * Datadog receives an Error as a separate structured argument, so redacting
 * only the visible message still leaks the original message through its stack.
 * BigQuery deliberately keeps the original payload for its internal grouping
 * contract; this copy is exclusively for the third-party Datadog sink.
 *
 * The copy never keeps the stack it was created with: WebKit's fetch
 * `TypeError` carries none, and that stack would point every such error at
 * this function and group them all under one Error Tracking issue.
 */
function sanitizeDatadogCause(error: unknown): Error | undefined {
    if (!(error instanceof Error)) return undefined;

    const sanitized = new Error(redactPersonalData(error.message));
    sanitized.name = allowlistedErrorName(error);
    sanitized.stack =
        typeof error.stack === "string"
            ? redactPersonalData(error.stack)
            : undefined;

    return sanitized;
}

/**
 * The suppression the two handlers below share.
 *
 * It exists for browser extensions and WebKit internals, which arrive in tight
 * identical bursts and once exhausted the ingest budget. What it also did was
 * hide the repeats that matter: a customer who hit the same fault three times
 * produced exactly one record, so "how many times" had no answer and every
 * repeat read as a single occurrence. It also dropped a recurrence forever —
 * the same error at 10:00 and again at 14:00 was one record, because the only
 * state was "what came last" with no notion of how long ago.
 *
 * Closed over by the handlers rather than held at module scope, so the state
 * belongs to the mount that installed them. At module scope it outlived every
 * consumer, which made the suppression leak between test cases and would have
 * survived a remount in the app.
 */
function createErrorSuppressor() {
    /**
     * Only ever the previous error, not a map keyed by message: that key space
     * would be fed by error text we do not author, and consecutive repeats are
     * the shape this is defending against.
     */
    let lastMessage = "";
    let lastStack = "";
    let occurrences = 0;

    /**
     * Which occurrence of this error to report it as, or `null` to suppress.
     *
     * Checks: bots, empty payloads, known noisy messages/sources, and how many
     * times this same error has already been reported.
     */
    const occurrenceToReport = (
        message: string,
        stack: string
    ): number | null => {
        if (isbot(navigator.userAgent)) return null;
        if (!message && !stack) return null;

        if (message) {
            const isInvalidMessage = EXCLUDE_MESSAGES.some(item => message.includes(item));
            if (isInvalidMessage) return null;
        }

        if (stack) {
            const isInvalidSource = EXCLUDE_SOURCES.some(item => stack.includes(item));
            if (isInvalidSource) return null;
        }

        const isRepeat = message === lastMessage && stack === lastStack;
        lastMessage = message;
        lastStack = stack;
        occurrences = isRepeat ? occurrences + 1 : 1;

        return occurrences <= MAX_REPORTED_OCCURRENCES ? occurrences : null;
    };

    return { occurrenceToReport };
}

/**
 * Initializes Datadog Browser Logs SDK and attaches global error handlers.
 *
 * Architecture (3 effects + ref sync):
 *   Effect 1 []: SDK init — runs once on mount
 *   Effect 2 [userId]: setUser — fires whenever userId changes
 *   Ref sync [userId, sendBigQueryEvent]: keeps refs current for error handlers
 *   Effect 3 []: global error handlers — read from refs to avoid stale closures
 *
 * forwardErrorsToLogs is OFF so we can apply custom filtering + dual-send to BigQuery.
 * Uses the same clientToken as DataDog RUM for automatic log↔session correlation.
 * Production-only; mount inside UserIdProvider in layout.tsx.
 */
export const DataDogLogs = () => {
    const sendBigQueryEvent = useUnauthorizedEventService();
    const { userId } = useUserId();

    // Refs to avoid stale closures in long-lived error handlers
    const userIdRef = useRef(userId);
    const sendBigQueryEventRef = useRef(sendBigQueryEvent);

    // Keep refs in sync with latest values
    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        sendBigQueryEventRef.current = sendBigQueryEvent;
    }, [sendBigQueryEvent]);

    // Effect 1: SDK init — runs once on mount (production only)
    useEffect(() => {
        if (!AppConfig.isProduction) {
            return;
        }

        datadogLogs.init({
            clientToken: "pub3325fb62d37e451430d9bf999d066474",
            site: "datadoghq.com",
            service: "innerbalance",
            env: "prod",
            version: APP_VERSION,
            forwardErrorsToLogs: false,
            sessionSampleRate: 100,
        });
    }, []);

    // Effect 2: Set user context — fires on every userId change
    useEffect(() => {
        if (!AppConfig.isProduction) {
            return;
        }

        if (userId) {
            datadogLogs.setUser({ id: userId });
        }
    }, [userId]);

    // Effect 3: Global error handlers — read from refs (no stale closures)
    useEffect(() => {
        if (!AppConfig.isProduction) {
            return;
        }

        const previousOnError = window.onerror;
        const previousUnhandledRejection = window.onunhandledrejection;
        // One suppressor for both handlers: the same error can reach us as an
        // uncaught throw and as a rejection, and counting those separately
        // would report one fault as two.
        const { occurrenceToReport } = createErrorSuppressor();

        const onErrorHandler = (message, source, lineno, colno, error) => {
            const msg = typeof message === "string" ? message : "Error event";
            const stack = error ? error.stack : null;
            const occurrence = occurrenceToReport(msg, stack ?? "");

            if (occurrence !== null) {
                const context = {
                    source,
                    lineno,
                    colno,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    user_id: userIdRef.current,
                    // Which try this was. Always present, so `1` is an answer
                    // rather than a field nobody set.
                    occurrence,
                    tag: "client",
                };
                const datadogMessage = redactPersonalData(msg);
                const cause = sanitizeDatadogCause(error);

                if (occurrence === FIRST_OCCURRENCE) {
                    datadogLogs.logger.error(datadogMessage, context, cause);

                    sendWebEvent(sendBigQueryEventRef.current, {
                        event_name: ERROR_BEACON_EVENT_NAME,
                        event_type: "user_non_interaction",
                        widget: "js_error",
                        meta_data: {
                            url: window.location.href,
                            error_text: stack,
                            error_msg_response: msg,
                        },
                    });
                } else {
                    datadogLogs.logger.warn(datadogMessage, context, cause);
                }
            }

            // Auto-reload on ChunkLoadError (deployment skew recovery).
            // Placed after logging so the error is tracked in Datadog/BigQuery.
            if (handleChunkLoadError(msg, error)) return;

            if (typeof previousOnError === "function") {
                return previousOnError(message, source, lineno, colno, error);
            }
        };

        const onUnhandledRejection = (event) => {
            const { msg, stack, reasonMessage } = describeRejection(event.reason);
            const occurrence = occurrenceToReport(msg, stack ?? "");

            if (occurrence !== null) {
                const context = {
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    user_id: userIdRef.current,
                    occurrence,
                    tag: "client",
                    errorName: allowlistedErrorName(event.reason),
                    reasonObject: readRejectionObjectType(event.reason),
                };
                const datadogMessage = redactPersonalData(msg);
                const cause = sanitizeDatadogCause(event.reason);

                if (occurrence === FIRST_OCCURRENCE) {
                    datadogLogs.logger.error(datadogMessage, context, cause);

                    sendWebEvent(sendBigQueryEventRef.current, {
                        event_name: ERROR_BEACON_EVENT_NAME,
                        event_type: "user_non_interaction",
                        widget: "js_error",
                        meta_data: {
                            url: window.location.href,
                            error_text: stack,
                            error_msg_response: msg,
                        },
                    });
                } else {
                    datadogLogs.logger.warn(datadogMessage, context, cause);
                }
            }

            // Auto-reload on ChunkLoadError (deployment skew recovery).
            // Placed after logging so the error is tracked in Datadog/BigQuery.
            //
            // `reasonMessage`, not `msg`: see `describeRejection`. This keeps
            // detection on rejections that carry their own `message`, which is
            // the only thing it ever matched.
            if (handleChunkLoadError(reasonMessage, event.reason)) return;

            if (typeof previousUnhandledRejection === "function") {
                return previousUnhandledRejection.bind(window)(event);
            }
        };

        window.onerror = onErrorHandler;
        window.addEventListener("unhandledrejection", onUnhandledRejection);

        return () => {
            window.onerror = previousOnError;
            window.removeEventListener("unhandledrejection", onUnhandledRejection);
        };
    }, []);

    return null;
};
